import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import BottomNav from '../BottomNav';
import FavoriteButton from './FavoriteButton';
import theme from '../theme';

const categories = ['Tutti', 'Babysitting', 'Dog sitting', 'Giardinaggio', 'Lavaggio auto', 'Pulizie in casa', 'Ripetizioni / Aiuto compiti', 'Trasloco / Facchinaggio', 'Videomaker / Fotografia', 'Taglio capelli/Acconciature', 'Trattamenti estetici', 'Altro'];

function BadgeVerificato({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" style={{ display: 'inline-block', verticalAlign: 'middle', marginLeft: 3, flexShrink: 0 }}>
      <path d="M8 1L2 3.5V8c0 3.3 2.5 5.8 6 7 3.5-1.2 6-3.7 6-7V3.5L8 1z" fill={theme.colors.primary}/>
      <path d="M5.5 8l2 2 3-3" stroke={theme.colors.white} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function resolveDisplayName(author) {
  if (!author) return 'Utente';
  if (!author.includes('@')) return author;
  const namePart = author.split('@')[0];
  return namePart.charAt(0).toUpperCase() + namePart.slice(1);
}

function getDistanceKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function MapView({ jobs, userPosition, myName }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    if (mapInstanceRef.current) return;

    const initMap = () => {
      const L = window.L;
      if (!L || !mapRef.current) return;

      const map = L.map(mapRef.current, {
        scrollWheelZoom: false,
        dragging: !L.Browser.mobile,
        tap: false,
      }).setView(userPosition, 13);
      mapInstanceRef.current = map;

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors'
      }).addTo(map);

      jobs.filter(j => j.lat && j.lng).forEach(job => {
        const isMyJob = job.author === myName;
        const color = isMyJob ? theme.colors.accent : theme.colors.primary;
        const icon = L.divIcon({
          className: '',
          html: `<div style="width:28px;height:28px;background:${color};border:3px solid #fff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 2px 6px rgba(0,0,0,0.3);"></div>`,
          iconSize: [28, 28],
          iconAnchor: [14, 28],
          popupAnchor: [0, -28],
        });

        const marker = L.marker([job.lat, job.lng], { icon }).addTo(map);
        marker.bindPopup(`
          <div style="min-width:160px">
            ${isMyJob ? `<div style="font-size:11px;color:${theme.colors.accent};font-weight:700;margin-bottom:4px">📌 Tuo annuncio</div>` : ''}
            ${job.isVerified ? `<div style="font-size:11px;color:${theme.colors.primary};font-weight:700;margin-bottom:4px">🛡️ Verificato</div>` : ''}
            <strong>${job.title}</strong><br/>
            ${job.price}<br/>
            <span style="color:${theme.colors.primary}">${job.category}</span><br/>
            ${job.city ? `📍 ${job.city}<br/>` : ''}
            ${job.distanceKm != null ? `🚶 ${job.distanceKm} km<br/>` : ''}
            <a href="/job/${job.id}" style="display:inline-block;margin-top:6px;background:${theme.colors.primary};color:#fff;padding:4px 10px;border-radius:6px;text-decoration:none;font-size:13px;">
              Vedi dettaglio →
            </a>
          </div>
        `);
      });
    };

    if (window.L) {
      initMap();
    } else {
      const interval = setInterval(() => {
        if (window.L) { clearInterval(interval); initMap(); }
      }, 100);
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [jobs, userPosition, myName]);

  return (
    <div style={{ position: 'relative' }}>
      <div ref={mapRef} style={{ height: 400, width: '100%', borderRadius: theme.radius.lg }} />
      <div style={{
        position: 'absolute', bottom: theme.spacing.sm, left: '50%', transform: 'translateX(-50%)',
        background: 'rgba(0,0,0,0.55)', color: theme.colors.white, fontSize: theme.font.size.xs,
        padding: '4px 10px', borderRadius: theme.radius.pill, pointerEvents: 'none', whiteSpace: 'nowrap'
      }}>
        Usa due dita per muovere la mappa
      </div>
    </div>
  );
}

function Home({ session, profile }) {
  const [jobs, setJobs] = useState([]);
  const [authorRatings, setAuthorRatings] = useState({});
  const [authorVerified, setAuthorVerified] = useState({});
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [activeCategory, setActiveCategory] = useState('Tutti');
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('lista');
  const [userPosition, setUserPosition] = useState(null);
  const [search, setSearch] = useState('');
  const [maxDistance, setMaxDistance] = useState('');
  const [distanceInput, setDistanceInput] = useState('');
  const navigate = useNavigate();

  const myName = profile?.full_name || resolveDisplayName(session?.user?.email);

  useEffect(() => {
    fetchJobs();
    fetchFavorites();

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setUserPosition([pos.coords.latitude, pos.coords.longitude]),
        () => setUserPosition([44.5, 10.5])
      );
    } else {
      setUserPosition([44.5, 10.5]);
    }

    if (!document.querySelector('#leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css';
      document.head.appendChild(link);
    }

    if (!document.querySelector('#leaflet-js')) {
      const script = document.createElement('script');
      script.id = 'leaflet-js';
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js';
      document.head.appendChild(script);
    }
  }, []);

  const fetchFavorites = async () => {
    const { data } = await supabase
      .from('favorites')
      .select('job_id')
      .eq('user_id', session.user.id);
    if (data) setFavoriteIds(new Set(data.map(f => f.job_id)));
  };

  const toggleFavorite = async (jobId) => {
    const isFav = favoriteIds.has(jobId);
    setFavoriteIds(prev => {
      const next = new Set(prev);
      if (isFav) next.delete(jobId); else next.add(jobId);
      return next;
    });
    if (isFav) {
      await supabase.from('favorites').delete().eq('user_id', session.user.id).eq('job_id', jobId);
    } else {
      await supabase.from('favorites').insert({ user_id: session.user.id, job_id: jobId });
    }
  };

  const fetchJobs = async () => {
    setLoading(true);
    const { data: jobsData, error } = await supabase.rpc('get_open_jobs');
    if (error || !jobsData) { setLoading(false); return; }

    const { data: profilesData } = await supabase
      .from('profiles')
      .select('full_name, identity_status');

    const verifiedMap = {};
    (profilesData || []).forEach(p => {
      verifiedMap[p.full_name] = p.identity_status === 'verificato';
    });
    setAuthorVerified(verifiedMap);

    const resolved = jobsData.map(job => ({
      ...job,
      author: resolveDisplayName(job.author),
    }));

    setJobs(resolved);
    fetchAuthorRatings(resolved);
    setLoading(false);
  };

  const fetchAuthorRatings = async (jobsList) => {
    const authorNames = [...new Set(jobsList.map(j => j.author))];
    if (authorNames.length === 0) return;

    const { data } = await supabase
      .from('reviews')
      .select('reviewed_email, stars, role')
      .eq('role', 'committente')
      .in('reviewed_email', authorNames);

    if (data) {
      const ratings = {};
      authorNames.forEach(name => {
        const authorReviews = data.filter(r => r.reviewed_email === name);
        if (authorReviews.length > 0) {
          const avg = authorReviews.reduce((sum, r) => sum + r.stars, 0) / authorReviews.length;
          ratings[name] = { avg: avg.toFixed(1), count: authorReviews.length };
        }
      });
      setAuthorRatings(ratings);
    }
  };

  const jobsWithDistance = jobs.map(job => {
    if (userPosition && job.lat && job.lng) {
      const km = getDistanceKm(userPosition[0], userPosition[1], job.lat, job.lng);
      return { ...job, distanceKm: Math.round(km * 10) / 10, isVerified: authorVerified[job.author] || false };
    }
    return { ...job, distanceKm: null, isVerified: authorVerified[job.author] || false };
  });

  const filtered = jobsWithDistance
    .filter(j => j.lat && j.lng)
    .filter(j => activeCategory === 'Tutti' || j.category === activeCategory)
    .filter(j => {
      if (!search) return true;
      const q = search.toLowerCase();
      return j.title?.toLowerCase().includes(q) ||
             j.description?.toLowerCase().includes(q) ||
             j.category?.toLowerCase().includes(q) ||
             j.author?.toLowerCase().includes(q) ||
             j.city?.toLowerCase().includes(q);
    })
    .filter(j => {
      if (!maxDistance || !j.distanceKm) return true;
      return j.distanceKm <= parseFloat(maxDistance);
    })
    .sort((a, b) => {
      if (a.distanceKm != null && b.distanceKm != null) return a.distanceKm - b.distanceKm;
      return 0;
    });

  const handleApplyDistance = () => setMaxDistance(distanceInput);
  const handleClearDistance = () => { setMaxDistance(''); setDistanceInput(''); };

  return (
    <div style={{ maxWidth: theme.layout.maxWidth, margin: '0 auto', fontFamily: theme.font.family }}>

      {/* Header */}
      <div style={{ background: theme.colors.primary, padding: theme.spacing.xl, color: theme.colors.white }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing.xs }}>
          <h1 style={{ margin: 0, fontSize: theme.font.size.hero }}>Jobbetty</h1>
          <button
            onClick={() => navigate('/cerca-utente')}
            style={{
              background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: theme.radius.circle,
              width: 38, height: 38, cursor: 'pointer', fontSize: theme.font.size.xl,
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
            🔍
          </button>
        </div>
        <p style={{ margin: `0 0 ${theme.spacing.md}px`, opacity: 0.85, fontSize: theme.font.size.md }}>📍 {filtered.length} lavori disponibili</p>
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: theme.font.size.xl }}>🔍</span>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Cerca lavori, categorie, persone..."
            style={{
              width: '100%', padding: '10px 12px 10px 36px',
              borderRadius: theme.radius.md, border: 'none', fontSize: theme.font.size.md,
              boxSizing: 'border-box', background: 'rgba(255,255,255,0.2)',
              color: theme.colors.white, outline: 'none'
            }}
          />
          {search && (
            <span onClick={() => setSearch('')} style={{
              position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
              cursor: 'pointer', color: theme.colors.white, fontSize: theme.font.size.xl
            }}>✕</span>
          )}
        </div>
      </div>

      <div style={{ padding: theme.spacing.lg, paddingBottom: 80 }}>
        <div style={{ display: 'flex', gap: theme.spacing.sm, overflowX: 'auto', marginBottom: theme.spacing.md, paddingBottom: theme.spacing.xs }}>
          {categories.map(cat => (
            <button key={cat} onClick={() => setActiveCategory(cat)} style={{
              background: activeCategory === cat ? theme.colors.primary : theme.colors.backgroundAlt,
              color: activeCategory === cat ? theme.colors.white : theme.colors.textSecondary,
              border: 'none', borderRadius: theme.radius.pill, padding: '6px 16px',
              cursor: 'pointer', whiteSpace: 'nowrap', fontSize: theme.font.size.base
            }}>{cat}</button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: theme.spacing.sm, marginBottom: theme.spacing.md, alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <input
              type="number"
              value={distanceInput}
              onChange={e => setDistanceInput(e.target.value)}
              placeholder="Distanza massima (km)"
              min="0"
              style={{ width: '100%', padding: '8px 12px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borderInput}`, fontSize: theme.font.size.base, boxSizing: 'border-box' }}
            />
          </div>
          <button onClick={handleApplyDistance} style={{
            background: theme.colors.primary, color: theme.colors.white, border: 'none',
            borderRadius: theme.radius.sm, padding: '8px 14px', fontSize: theme.font.size.base, cursor: 'pointer', whiteSpace: 'nowrap'
          }}>🔍 Filtra</button>
          {maxDistance && (
            <button onClick={handleClearDistance} style={{
              background: theme.colors.backgroundAlt, color: theme.colors.textSecondary, border: 'none',
              borderRadius: theme.radius.sm, padding: '8px 12px', fontSize: theme.font.size.base, cursor: 'pointer'
            }}>✕</button>
          )}
        </div>

        {maxDistance && (
          <div style={{ fontSize: theme.font.size.base, color: theme.colors.primary, marginBottom: theme.spacing.md }}>
            📍 Mostrando annunci entro <strong>{maxDistance} km</strong> da te
          </div>
        )}

        <div style={{ display: 'flex', background: theme.colors.backgroundAlt, borderRadius: theme.radius.md, padding: 3, marginBottom: theme.spacing.lg }}>
          <button onClick={() => setView('lista')} style={{
            flex: 1, padding: theme.spacing.sm, border: 'none', borderRadius: theme.radius.sm, cursor: 'pointer', fontSize: theme.font.size.md,
            background: view === 'lista' ? theme.colors.primary : 'transparent',
            color: view === 'lista' ? theme.colors.white : theme.colors.textSecondary,
          }}>📋 Lista</button>
          <button onClick={() => setView('mappa')} style={{
            flex: 1, padding: theme.spacing.sm, border: 'none', borderRadius: theme.radius.sm, cursor: 'pointer', fontSize: theme.font.size.md,
            background: view === 'mappa' ? theme.colors.primary : 'transparent',
            color: view === 'mappa' ? theme.colors.white : theme.colors.textSecondary,
          }}>🗺️ Mappa</button>
        </div>

        {search && (
          <div style={{ fontSize: theme.font.size.base, color: theme.colors.textMuted, marginBottom: theme.spacing.md }}>
            {filtered.length} risultati per "<strong>{search}</strong>"
          </div>
        )}

        {view === 'lista' && (
          <>
            {loading && <p style={{ textAlign: 'center', color: theme.colors.textMuted }}>Caricamento...</p>}
            {!loading && filtered.length === 0 && (
              <p style={{ textAlign: 'center', color: theme.colors.textMuted, marginTop: 40 }}>
                {maxDistance ? `Nessun annuncio entro ${maxDistance} km.` : search ? `Nessun risultato per "${search}"` : 'Nessun annuncio trovato.'}
              </p>
            )}
            {filtered.map(job => {
              const isMyJob = job.author === myName;
              return (
                <div key={job.id} onClick={() => navigate(`/job/${job.id}`)}
                  style={{
                    borderRadius: theme.radius.lg, marginBottom: theme.spacing.md, cursor: 'pointer',
                    position: 'relative', background: theme.colors.white, overflow: 'hidden',
                    boxShadow: isMyJob
                      ? `inset 4px 0 0 ${theme.colors.accent}, ${theme.shadow.card}`
                      : theme.shadow.card,
                    border: `1px solid ${theme.colors.border}`,
                  }}>
                  {isMyJob && (
                    <div style={{
                      position: 'absolute', top: 10, right: 10,
                      background: theme.colors.accent, color: theme.colors.white,
                      fontSize: theme.font.size.xs, fontWeight: 700,
                      padding: '2px 8px', borderRadius: theme.radius.pill,
                    }}>📌 Tuo</div>
                  )}
                  {!isMyJob && (
                    <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 2 }}>
                      <FavoriteButton isFavorite={favoriteIds.has(job.id)} onToggle={() => toggleFavorite(job.id)} />
                    </div>
                  )}
                  <div style={{ padding: theme.spacing.lg }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: theme.spacing.sm, paddingRight: (isMyJob || !isMyJob) ? 50 : 0 }}>
                      <span style={{ fontWeight: 600, fontSize: theme.font.size.md }}>{job.title}</span>
                      <span style={{ color: theme.colors.primary, fontWeight: 600 }}>{job.price}</span>
                    </div>
                    <div style={{ fontSize: theme.font.size.base, color: theme.colors.textSecondary, display: 'flex', gap: theme.spacing.md, flexWrap: 'wrap', alignItems: 'center' }}>
                      <span style={{ display: 'flex', alignItems: 'center' }}>
                        👤 {job.author}
                        {job.isVerified && <BadgeVerificato size={13} />}
                      </span>
                      {authorRatings[job.author] && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 3, background: theme.colors.warningBgSoft, padding: '2px 8px', borderRadius: theme.radius.md }}>
                          <span style={{ color: theme.colors.starGold, fontSize: theme.font.size.base }}>★</span>
                          <span style={{ fontWeight: 700, fontSize: theme.font.size.sm, color: theme.colors.textPrimary }}>{authorRatings[job.author].avg}</span>
                          <span style={{ fontSize: theme.font.size.xs, color: theme.colors.textMuted }}>({authorRatings[job.author].count})</span>
                        </span>
                      )}
                      <span style={{ background: theme.colors.primaryLight, color: theme.colors.primary, padding: '2px 8px', borderRadius: theme.radius.md, fontSize: theme.font.size.sm }}>{job.category}</span>
                      {job.city && <span style={{ color: theme.colors.textMuted, fontSize: theme.font.size.sm }}>📍 {job.city}</span>}
                      {job.distanceKm != null && (
                        <span style={{ color: theme.colors.primary, fontSize: theme.font.size.sm, fontWeight: 600 }}>🚶 {job.distanceKm} km</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )}

        {view === 'mappa' && (
          <div style={{ marginBottom: theme.spacing.lg }}>
            <MapView jobs={filtered} userPosition={userPosition || [44.5, 10.5]} myName={myName} />
            {filtered.filter(j => j.lat && j.lng).length === 0 && (
              <p style={{ textAlign: 'center', color: theme.colors.textMuted, marginTop: 10, fontSize: theme.font.size.base }}>
                Nessun annuncio trovato in questa zona.
              </p>
            )}
            <div style={{ display: 'flex', gap: theme.spacing.lg, marginTop: 10, fontSize: theme.font.size.sm, color: theme.colors.textSecondary, justifyContent: 'center' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 12, height: 12, borderRadius: theme.radius.circle, background: theme.colors.primary, display: 'inline-block' }} />
                Annunci altrui
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 12, height: 12, borderRadius: theme.radius.circle, background: theme.colors.accent, display: 'inline-block' }} />
                I tuoi annunci
              </span>
            </div>
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}

export default Home;